export const REENGAGEMENT_EMAIL_PAGE_SIZE = 50;

export const OUTREACH_GROUP_FILTERS = new Set([
  "all",
  "needs_assessment_not_scheduled_yet",
  "scheduled_appointment_coming_up_but_not_this_calendar_month",
  "no_appointment_scheduled",
  "unclassified",
]);

export const BOOLEAN_FILTERS = new Set(["all", "yes", "no"]);

export type ReengagementSummary = {
  bookedCarespaces: number;
  changedGroupCarespaces: number;
  emailsSent: number;
  encounteredCarespaces: number;
  observedWaveCount: number;
  repeatedCarespaces: number;
  repliedCarespaces: number;
  uniqueCarespacesTouched: number;
};

export type ReengagementGroupMetrics = {
  bookedCount: number;
  carespacesSent: number;
  deliveredCount: number;
  deliveryIssueCount: number;
  emailsSent: number;
  encounteredCount: number;
  label: string;
  outreachGroup: string;
  repliedCount: number;
  sortOrder: number;
};

export type ReengagementWaveGroup = ReengagementGroupMetrics & {
  waveDate: string;
};

export type ReengagementWaveTotals = {
  bookedCount: number;
  carespacesSent: number;
  deliveredCount: number;
  deliveryIssueCount: number;
  emailsSent: number;
  encounteredCount: number;
  repliedCount: number;
};

export type ReengagementWave = {
  groups: ReengagementWaveGroup[];
  totals: ReengagementWaveTotals;
  waveDate: string;
  waveNumber: number;
};

export type ReengagementEmailReply = {
  message: string;
  sentAt: string;
};

export type ReengagementEmailRow = {
  activityId: string;
  booked: boolean;
  carespaceId: string;
  delivered: boolean;
  deliveryIssue: boolean;
  encountered: boolean;
  groupLabel: string;
  message: string;
  outreachGroup: string;
  replied: boolean;
  replies: ReengagementEmailReply[];
  sentAt: string;
};

export type ReengagementEmailPage = {
  page: number;
  pageSize: number;
  rows: ReengagementEmailRow[];
  totalCount: number;
};

export type ReengagementReportResponse = {
  month: string;
  monthStart: string;
  monthlyGroups: ReengagementGroupMetrics[];
  nextMonthStart: string;
  outcomesObservedThrough: string;
  previousMonth: {
    month: string;
    outcomesObservedThrough: string;
    summary: ReengagementSummary;
  };
  summary: ReengagementSummary;
  waves: ReengagementWave[];
};

export type ReengagementWaveEmailsResponse = {
  emailPage: ReengagementEmailPage;
  month: string;
  outcomesObservedThrough: string;
  waveDate: string;
};

export type ReengagementEmailFilters = {
  booked: string;
  encountered: string;
  outreachGroup: string;
  replied: string;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isValidMonthKey(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return false;
  const month = Number(match[2]);
  return Number.isInteger(month) && month >= 1 && month <= 12;
}

function isValidIsoDate(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function isValidDateTime(value: unknown): value is string {
  return typeof value === "string" && !Number.isNaN(Date.parse(value));
}

function hasNumericFields(value: Record<string, unknown>, fields: string[]): boolean {
  return fields.every(
    (field) => typeof value[field] === "number" && Number.isFinite(value[field] as number),
  );
}

const SUMMARY_FIELDS = [
  "observedWaveCount",
  "uniqueCarespacesTouched",
  "emailsSent",
  "repeatedCarespaces",
  "changedGroupCarespaces",
  "repliedCarespaces",
  "bookedCarespaces",
  "encounteredCarespaces",
] as const;

const GROUP_METRIC_FIELDS = [
  "sortOrder",
  "carespacesSent",
  "emailsSent",
  "deliveredCount",
  "deliveryIssueCount",
  "repliedCount",
  "bookedCount",
  "encounteredCount",
] as const;

const WAVE_TOTAL_FIELDS = GROUP_METRIC_FIELDS.filter((field) => field !== "sortOrder");

function isValidSummary(value: unknown): value is ReengagementSummary {
  return isPlainObject(value) && hasNumericFields(value, [...SUMMARY_FIELDS]);
}

function isValidGroup(value: unknown): value is ReengagementGroupMetrics {
  return (
    isPlainObject(value) &&
    typeof value.outreachGroup === "string" &&
    typeof value.label === "string" &&
    hasNumericFields(value, [...GROUP_METRIC_FIELDS])
  );
}

function isValidWave(value: unknown): value is ReengagementWave {
  return (
    isPlainObject(value) &&
    isValidIsoDate(value.waveDate) &&
    typeof value.waveNumber === "number" &&
    Number.isFinite(value.waveNumber) &&
    isPlainObject(value.totals) &&
    hasNumericFields(value.totals, [...WAVE_TOTAL_FIELDS]) &&
    Array.isArray(value.groups) &&
    value.groups.every(
      (group) => isValidGroup(group) && isValidIsoDate((group as ReengagementWaveGroup).waveDate),
    )
  );
}

function isValidReply(value: unknown): value is ReengagementEmailReply {
  return isPlainObject(value) && isValidDateTime(value.sentAt) && typeof value.message === "string";
}

function isValidEmailRow(value: unknown): value is ReengagementEmailRow {
  return (
    isPlainObject(value) &&
    typeof value.activityId === "string" &&
    typeof value.carespaceId === "string" &&
    isValidDateTime(value.sentAt) &&
    typeof value.outreachGroup === "string" &&
    typeof value.groupLabel === "string" &&
    typeof value.message === "string" &&
    typeof value.delivered === "boolean" &&
    typeof value.deliveryIssue === "boolean" &&
    typeof value.replied === "boolean" &&
    Array.isArray(value.replies) &&
    value.replies.every(isValidReply) &&
    typeof value.booked === "boolean" &&
    typeof value.encountered === "boolean"
  );
}

function isValidEmailPage(value: unknown): value is ReengagementEmailPage {
  return (
    isPlainObject(value) &&
    Number.isInteger(value.page) &&
    (value.page as number) >= 1 &&
    value.pageSize === REENGAGEMENT_EMAIL_PAGE_SIZE &&
    Number.isInteger(value.totalCount) &&
    (value.totalCount as number) >= 0 &&
    Array.isArray(value.rows) &&
    value.rows.every(isValidEmailRow)
  );
}

export function isReengagementReportResponse(data: unknown): data is ReengagementReportResponse {
  return (
    isPlainObject(data) &&
    isValidMonthKey(data.month) &&
    isValidIsoDate(data.monthStart) &&
    isValidIsoDate(data.nextMonthStart) &&
    isValidIsoDate(data.outcomesObservedThrough) &&
    isValidSummary(data.summary) &&
    isPlainObject(data.previousMonth) &&
    isValidMonthKey(data.previousMonth.month) &&
    isValidIsoDate(data.previousMonth.outcomesObservedThrough) &&
    isValidSummary(data.previousMonth.summary) &&
    Array.isArray(data.monthlyGroups) &&
    data.monthlyGroups.every(isValidGroup) &&
    Array.isArray(data.waves) &&
    data.waves.every(isValidWave)
  );
}

export function isReengagementWaveEmailsResponse(
  data: unknown,
): data is ReengagementWaveEmailsResponse {
  return (
    isPlainObject(data) &&
    isValidMonthKey(data.month) &&
    isValidIsoDate(data.waveDate) &&
    isValidIsoDate(data.outcomesObservedThrough) &&
    isValidEmailPage(data.emailPage)
  );
}
